import React from 'react';
import { AspectRatioInner, AspectRatioOuter, OverlayNotABackdrop } from 'Shared/SharedComponents/atoms/AspectRatio';
import { styled, StyledProps } from '@glitz/react';
import { setImageBackground, Preset } from 'Shared/image-background';
import VideoSourceType from 'Shared/ViewModels/VideoSourceViewModel.type';
import VideoPlayer from 'Shared/Video/VideoPlayer'
import { isCompact } from 'Shared/Viewport';

type BasePropTypes = {
  children?: React.ReactNode;
  image?: { url?: string };
  videoSources?: VideoSourceType[];
  breakPoint: number;
  preset?: Preset;
};

type ImageVideoBackgroundPropTypes = BasePropTypes & {
  borderRadius?: number;
  paddingTop: string;
  className?: string;
};

type ViewportSizeVideoBackgroundPropTypes = BasePropTypes & {
  fixedVideo?: boolean;
  borderRadius?: number;
  height?: string;
  gradientBg?: boolean;
};

export const AspectImageVideoBackground = (
  (
    props: ImageVideoBackgroundPropTypes & {},
  ) => {
    const { children,
      image,
      paddingTop,
      videoSources,
      borderRadius,
      breakPoint,
      className,
      preset } = props;

    const isMobile = isCompact(breakPoint);
    const gotVideo = (videoSources?.length ?? 0) > 0;
    return (
      <AspectRatioOuter
        className={className}
        css={{
          paddingTop: paddingTop,
        }}
      >
        <AspectRatioInner
          css={{
            background: {
              ...(!gotVideo && image && {
                image: setImageBackground(image?.url, preset),
                size: 'cover',
                position: 'center center',
              }),
            },
            borderRadius: borderRadius && borderRadius > 0 ? `${borderRadius}px` : '0px'
          }}
        >
          {gotVideo && (
            <OverlayNotABackdrop>
              <VideoPlayer
                autoPlay
                loop
                muted
                playsInline
                coverContent
                isMobile={isMobile}
                sources={videoSources}
              />
            </OverlayNotABackdrop>
          )}
          {children}
        </AspectRatioInner>
      </AspectRatioOuter>
    );
  }
);

export const ViewportSizeVideoBackground = styled(
  (
    props: ViewportSizeVideoBackgroundPropTypes & StyledProps,
  ) => {
    const { children,
      image,
      videoSources,
      breakPoint,
      fixedVideo = false,
      //css,
      borderRadius,
      preset,
      height = '100%',
      compose,
    } = props;
    const hasVideo = (videoSources?.length ?? 0) > 0;
    const isMobile = isCompact(breakPoint);
    return (
      <styled.Div
        css={compose({
          height: height,
          width: '100%',
          position: 'relative',
          ...(image && !hasVideo && {
            background: {
              image: setImageBackground(image?.url, preset),
              size: 'cover',
              position: 'center center',
            },
          }),
          ...(!hasVideo && {
            borderRadius: borderRadius && borderRadius > 0 ? `${borderRadius}px` : '0px'
          }),
          ...(fixedVideo && {
            clipPath: 'inset(0 0 0 0)',
            overflow: 'hidden',
          }),
        })}
      >
        {hasVideo && (
          <OverlayNotABackdrop>
            <VideoPlayer
              autoPlay
              loop
              muted
              coverContent
              playsInline
              fixedVideo={fixedVideo}
              isMobile={isMobile}
              sources={videoSources}
            />
          </OverlayNotABackdrop>
        )}
        {children}
      </styled.Div>
    );
  }
);

type AutoSizeVideoBackgroundPropTypes = BasePropTypes & {
  borderRadius: number;
  gradientBg?: boolean;
};

export const AutoSizeVideoBackground = styled(
  (
    props: AutoSizeVideoBackgroundPropTypes & StyledProps,
  ) => {
    const { children,
      image,
      videoSources,
      breakPoint,
      borderRadius,
      preset,
      compose,
    } = props;
    return (
      <ViewportSizeVideoBackground image={image} borderRadius={borderRadius} videoSources={videoSources} preset={preset} breakPoint={breakPoint} css={compose({ height: 'auto' })} fixedVideo>
        {children}
      </ViewportSizeVideoBackground>
    );
  });
